import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Star,
  ExternalLink,
  Briefcase,
  Plus,
  Loader2,
  Camera,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  getTechniqueCaption,
  getTechniqueMotionGuide,
} from "./TechniqueSchematic";
import SkinSignalBadge from "./SkinSignalBadge";
import ProductShadePicker from "./ProductShadePicker";
import { SHADE_MATCHING_UI_ENABLED } from "../lib/featureFlags";
import TrouveTea from "./TrouveTea";
import { trackFP } from "../lib/firstPartyAnalytics";
import { API } from "../lib/api";

/**
 * The single, dominant section of `/routine/:id` — title + technique
 * schematic + how-to + arsenal callout + product recommendations + nav.
 *
 * Extracted from Routine.jsx (Feb 13 2026) to take the parent from
 * 1,177 lines to ~820 — the routine viewer was the only file in the
 * app heavy enough to make tree-shaking / hot reload sluggish.
 *
 * No state of its own — every interactive bit is lifted to the parent
 * so the parent stays the single source of truth for `activeStep`,
 * `completed`, and the arsenal mutation set. Keep it that way; if you
 * find yourself adding `useState` here, lift it up instead.
 */
const RoutineActiveStep = ({
  routine,
  activeStep,
  currentStep,
  completed,
  onToggleComplete,
  stepProducts,
  arsenalForStep,
  arsenalByBrandName,
  arsenalAdding,
  onAddProductToArsenal,
  cvExplainById,
  cvSkinReasonById,
  cvShadesById,
  skinSignals,
  cvRecommendations,
  onPrev,
  onNext,
  onOpenMirror,
  user,
  sessionPhoto,
  cardRef,
  ephemeral = false,
}) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        ref={cardRef}
        key={activeStep}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.4 }}
        className="bg-white border border-[#EADDD7] rounded-3xl p-5 sm:p-10 soft-shadow scroll-mt-24"
        data-testid="routine-step-card"
      >
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#9B6A6C] mb-2">
              {t("routine.stepOf", {
                current: activeStep + 1,
                total: routine.steps.length,
              })}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#2C2123] leading-tight">
              {currentStep.title}
            </h2>
          </div>
          <button
            onClick={() => onToggleComplete(activeStep)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-all ${
              completed.has(activeStep)
                ? "bg-[#9B6A6C] text-white"
                : "border border-[#D5A8A4] text-[#5D4050] hover:bg-[#F8EEEE]"
            }`}
            data-testid="routine-mark-done-btn"
          >
            <Check className="h-4 w-4" />
            {completed.has(activeStep)
              ? t("routine.doneBadge")
              : t("routine.markDone")}
          </button>
        </div>

        {/* ── "Meet me at the mirror" — primary action card (iter 77,
            Jun 24 2026). Replaces the previous static face diagram as
            the main instructional element. The big diagram is gone;
            a tiny decorative schematic (~80px) is kept inside this
            card as a soft supporting visual for no-camera / desktop
            users. The card sits at the TOP of the step area so the
            AR mirror is the first thing the user sees on each step,
            not a buried footer link. */}
        {onOpenMirror && (
          <div
            className="mb-6 rounded-3xl border border-[#EADDD7] bg-gradient-to-br from-[#FFF6F2] to-[#F5EFEB] p-5 sm:p-6"
            data-testid="meet-mirror-card"
          >
            {/* Feb 27 2026: the small decorative TechniqueSchematic
                thumbnail that used to live at the top-left of this
                card was removed at founder request — it added visual
                noise without teaching value, since the AR mirror is
                the actual teaching surface. Content now reflows to
                the card's left edge with no thumbnail column. */}
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#9B6A6C] flex items-center gap-1.5 mb-1.5">
                <Sparkles className="h-3 w-3" />
                <span>{t("meetMirror.kicker")}</span>
              </div>
              <p
                className="text-[15px] sm:text-base text-[#2C2123] leading-snug mb-3.5"
                data-testid="meet-mirror-title"
              >
                {t("meetMirror.title")}
              </p>

              <p
                className="text-[12.5px] text-[#5C4B51] leading-relaxed mb-4 italic"
                data-testid="meet-mirror-helper"
              >
                {t("meetMirror.modeGuideHelper")}
              </p>

              <button
                type="button"
                  onClick={() => {
                    trackFP("mirror_step_cta_clicked", {
                      step_idx: activeStep,
                      step_category: currentStep?.category || null,
                      step_where: currentStep?.where || null,
                      routine_id: routine?.id || null,
                      mode: "guide",
                    });
                    onOpenMirror("guide");
                  }}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-5 py-3 sm:py-2.5 text-[15px] sm:text-[14px] font-medium transition-all shadow-[0_6px_18px_rgba(155,106,108,0.32)] hover:-translate-y-0.5 min-h-[48px] sm:min-h-0"
                  data-testid="meet-mirror-cta"
                >
                  <Camera className="h-4 w-4" />
                  {t("meetMirror.ctaGuide")}
                  <ChevronRight className="h-4 w-4" />
                </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-1 gap-6 mb-2">
          <div className="space-y-5">
            <div className="bg-[#FDFBF7] border border-[#EADDD7] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-xs uppercase tracking-[0.2em] text-[#9B6A6C] flex items-center gap-2">
                  <Sparkles className="h-3 w-3" /> {t("routine.howToApply")}
                </div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#8A7D82]">
                  {t("routine.tool")} ·{" "}
                  {getTechniqueMotionGuide(currentStep.category).tool}
                </div>
              </div>
              <p className="text-[#2C2123] leading-relaxed text-[15px] mb-3 italic">
                {getTechniqueCaption(currentStep.category)}
              </p>
              <ol
                className="space-y-2"
                data-testid="routine-step-motion-guide"
              >
                {getTechniqueMotionGuide(currentStep.category).steps.map(
                  (s, i) => (
                    <li
                      key={`${currentStep.category}-step-${i}`}
                      className="flex items-start gap-3 text-[14px] text-[#3D2F33]"
                    >
                      <span className="shrink-0 mt-0.5 h-5 w-5 rounded-full bg-[#9B6A6C] text-white text-[11px] font-medium flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="leading-snug">{s}</span>
                    </li>
                  ),
                )}
              </ol>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8A7D82] mb-1">
                {t("routine.what")}
              </div>
              <p className="text-[#2C2123] leading-relaxed">
                {currentStep.what}
              </p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#8A7D82] mb-1">
                {t("routine.where")}
              </div>
              <p className="text-[#2C2123] leading-relaxed">
                {currentStep.where}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-gradient-to-br from-[#FDFBF7] to-[#F2E8E9] border border-[#EADDD7] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#9B6A6C] mb-1 flex items-center gap-2">
              <span aria-hidden="true">☕</span> {t("routine.trouveTeaWhy")}
            </div>
            <p className="text-[#2C2123] leading-relaxed">
              {currentStep.why}
            </p>
          </div>
          {currentStep.tip && (
            <div className="flex items-start gap-3 text-sm text-[#5C4B51] bg-[#F5EFEB] rounded-xl p-4">
              <Star className="h-4 w-4 mt-0.5 shrink-0 text-[#D4B483]" />
              <span>{currentStep.tip}</span>
            </div>
          )}
        </div>

        {/* Beauty Arsenal — owned products matching this step.
            Renders above product recommendations so users see
            their own kit first. Affirming language: "use what
            you have" beats "buy something new" every time. */}
        {arsenalForStep.length > 0 && (
          <div
            className="mt-8 rounded-2xl bg-gradient-to-br from-[#F5DCD7]/40 to-[#FAF5F1] border border-[#EADDD7] p-4 sm:p-5"
            data-testid="routine-step-arsenal"
          >
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-white border border-[#EADDD7] flex items-center justify-center shrink-0">
                <Briefcase className="h-4 w-4 text-[#9B6A6C]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-[0.18em] text-[#9B6A6C] mb-1.5 font-medium">
                  {t("routine.fromYourArsenal")}
                </div>
                <p className="text-sm text-[#2C2123] leading-relaxed mb-2">
                  {arsenalForStep.length === 1
                    ? t("routine.ownOneForStep")
                    : t("routine.ownNForStep", {
                        count: arsenalForStep.length,
                      })}
                </p>
                <ul className="space-y-1.5">
                  {arsenalForStep.map((it) => (
                    <li
                      key={it.id}
                      className="text-sm text-[#3D2F33] leading-snug"
                      data-testid={`routine-step-arsenal-item-${it.id}`}
                    >
                      <span className="font-medium">{it.brand}</span>{" "}
                      <span className="text-[#5C4B51]">{it.name}</span>
                      {it.shade && (
                        <span className="text-[#9B6A6C] italic">
                          {" · "}
                          {it.shade}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Product recommendations */}
        {stepProducts.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <div className="text-xs uppercase tracking-[0.2em] text-[#8A7D82]">
                {t("routine.gorgeousPicks")}
              </div>
              {/* Brand-affinity learning badge — renders once the
                  user has produced enough signals (shop clicks +
                  arsenal saves) for personalisation to feel real.
                  Soft pill, not pushy; the persona's idea, not
                  marketing-speak. */}
              {routine.personalisation?.has_brand_learning && (
                <span
                  className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] text-[#5D4050] bg-[#F5DCD7]/70 border border-[#EADDD7] rounded-full px-2.5 py-0.5"
                  data-testid="routine-personalisation-badge"
                  title={
                    routine.personalisation.top_brands?.length
                      ? t("routine.personalisedTooltip", {
                          brands: routine.personalisation.top_brands.join(", "),
                        })
                      : t("routine.personalisedTooltipDefault")
                  }
                >
                  <Sparkles className="h-3 w-3 text-[#9B6A6C]" />
                  {t("routine.personalisedForYou")}
                </span>
              )}
            </div>
            <SkinSignalBadge
              signals={skinSignals}
              category={currentStep.category}
              cvCategories={Object.keys(cvRecommendations || {})}
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stepProducts.map((p) => (
                <div
                  key={p.id}
                  className="rounded-2xl border border-[#EADDD7] p-4 hover:border-[#D5A8A4] transition-colors flex flex-col"
                  data-testid={`routine-product-${p.id}`}
                >
                  <div className="text-xs text-[#9B6A6C] uppercase tracking-[0.18em] mb-1">
                    {p.brand}
                  </div>
                  <div className="text-[#2C2123] font-medium leading-tight mb-1">
                    {p.name}
                  </div>
                  <div className="text-sm text-[#5C4B51] leading-relaxed">
                    {p.description}
                  </div>
                  {cvExplainById[p.id] && (
                    <TrouveTea
                      text={cvExplainById[p.id]}
                      subjectId={p.id}
                      subjectType="product"
                      testIdSuffix={`product-${p.id}`}
                    />
                  )}
                  {cvSkinReasonById[p.id] && (
                    <div
                      className="mt-2 text-xs text-[#5D4050] bg-[#F4EEF2] border border-[#E4D2DC] rounded-lg px-3 py-2 leading-snug"
                      data-testid={`routine-product-skin-reason-${p.id}`}
                    >
                      <span className="uppercase tracking-[0.16em] text-[10px] text-[#9B6A6C] mr-1 font-medium">
                        {t("routine.forYourSkin")}
                      </span>
                      {cvSkinReasonById[p.id]}
                    </div>
                  )}
                  {SHADE_MATCHING_UI_ENABLED && cvShadesById[p.id] && (
                    <ProductShadePicker
                      productId={p.id}
                      shades={cvShadesById[p.id]}
                    />
                  )}
                  <div className="text-xs text-[#8A7D82] mt-3 italic">
                    {p.why_inclusive}
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#F5EFEB] flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-xs text-[#8A7D82]">
                      {p.price_band} · {p.shade_range}
                    </span>
                    <div className="flex items-center gap-3">
                      {/* Arsenal toggle — affirming, one-tap. Shows
                          a steady "In your arsenal" badge when the
                          user has already logged this exact product
                          by brand + name; otherwise a quiet
                          "+ Add to arsenal" button. */}
                      {!ephemeral && (() => {
                        const key = `${(p.brand || "").trim().toLowerCase()}|${(p.name || "").trim().toLowerCase()}`;
                        const inArsenal = arsenalByBrandName.has(key);
                        const adding = arsenalAdding.has(p.id);
                        if (inArsenal) {
                          return (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-[#5D4050] bg-[#F5DCD7]/60 border border-[#EADDD7] rounded-full px-2.5 py-1"
                              data-testid={`routine-product-arsenal-owned-${p.id}`}
                              aria-label={`${p.brand} ${p.name} is in your arsenal`}
                            >
                              <Check className="h-3 w-3" />
                              {t("routine.inArsenal")}
                            </span>
                          );
                        }
                        return (
                          <button
                            type="button"
                            onClick={() => onAddProductToArsenal(p)}
                            disabled={adding}
                            className="inline-flex items-center gap-1 text-xs text-[#9B6A6C] hover:text-[#7d5455] disabled:opacity-50 transition-colors"
                            data-testid={`routine-product-arsenal-add-${p.id}`}
                            aria-label={`Add ${p.brand} ${p.name} to my arsenal`}
                          >
                            {adding ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3" />
                            )}
                            {adding
                              ? t("routine.saving")
                              : t("routine.addToArsenal")}
                          </button>
                        );
                      })()}
                      <a
                        href={ephemeral ? p.url : `${API}/go/${p.id}`}
                        target="_blank"
                        rel="sponsored noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[#9B6A6C] hover:text-[#805557] font-medium transition-colors"
                        data-testid={`routine-product-shop-${p.id}`}
                      >
                        {t("routine.shop")}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* FTC + Amazon Associates disclosure — required by
                Amazon's Operating Agreement to appear visibly
                wherever tagged links are displayed. */}
            <p
              className="mt-4 text-[11px] text-[#8A7D82] leading-relaxed"
              data-testid="routine-affiliate-disclosure"
            >
              {t("routine.affiliateDisclosure")}
            </p>
            {/* Ingredient honesty — formulations change, label is
                the truth. Surfaces alongside every recommendation
                so allergy-conscious users always see it. */}
            <p
              className="mt-2 text-[11px] text-[#8A7D82] leading-relaxed"
              data-testid="routine-ingredient-disclaimer"
            >
              {t("routine.ingredientDisclaimer")}
            </p>
          </div>
        )}

        {/* The single guided-mirror entry point lives at the top of
            the step card, so there is no duplicate footer CTA here. */}

        {/* Nav — iter 84 (Feb 28 2026) mobile button audit.
            Prev was previously a tiny text link with no border, easy
            to miss with a thumb. Now it's a pill with a 44pt touch
            target. Next is full-width on mobile so it can't be missed,
            inline-pill on desktop. The Finish ("I'm glowing") variant
            matches Next's mobile-full-width treatment. */}
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            onClick={onPrev}
            disabled={activeStep === 0}
            className="order-2 sm:order-none inline-flex items-center justify-center gap-1.5 rounded-full border border-[#EADDD7] px-5 py-3 text-sm font-medium text-[#5C4B51] hover:text-[#2C2123] hover:border-[#9B6A6C] hover:bg-[#FAF6F2] disabled:opacity-40 disabled:hover:bg-transparent transition-colors min-h-[44px] sm:px-4 sm:py-2 sm:font-normal"
            data-testid="routine-prev-btn"
          >
            <ChevronLeft className="h-4 w-4" /> {t("routine.prevStep")}
          </button>
          {activeStep < routine.steps.length - 1 ? (
            <button
              onClick={onNext}
              className="order-1 sm:order-none w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3.5 sm:py-3 text-base sm:text-sm font-medium transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 min-h-[48px] sm:min-h-0"
              data-testid="routine-next-btn"
            >
              {t("routine.nextStep")}
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => {
                onToggleComplete(activeStep);
                toast.success(t("routine.youDidItToast"));
              }}
              className="order-1 sm:order-none w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#5D4050] text-white hover:bg-[#4a3340] rounded-full px-6 py-3.5 sm:py-3 text-base sm:text-sm font-medium transition-all shadow-[0_6px_20px_rgba(93,64,80,0.3)] hover:-translate-y-0.5 min-h-[48px] sm:min-h-0"
              data-testid="routine-finish-btn"
            >
              <Check className="h-4 w-4" /> {t("routine.imGlowing")}
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RoutineActiveStep;
